<script lang="ts" setup>
import { Popup, Search, showFailToast, Tag, TextEllipsis } from "vant";
import { Mate } from "@/types/entity";
import { onMounted, ref } from "vue";

import UserAvatar from "@/components/UserAvatar.vue";
import GenderBadge from "@/components/MateStyle/GenderBadge.vue";
import ThreadList from "@/components/mobile/ThreadList.vue";
import { useSearch } from "@/stores/search";
import i18n from "@/locales";

const props = defineProps<{
  show: boolean;
  atMateIds: Pick<Mate, "name" | "uuid">[];
}>();

const emit = defineEmits<{
  (e: "update:atMateIds", mates: Pick<Mate, "name" | "uuid">[]): void;
  (e: "update:show", isShow: boolean): void;
}>();

const isLoading = ref<boolean>(false);
const searchStore = useSearch();
const searchMsg = ref<string>("");
const selectedMateIdArr = ref<string[]>([]);
const selectedMateArr = ref<Pick<Mate, "name" | "uuid">[]>([]);

const MAX_SELECTED_COUNT = 10;

onMounted(() => {
  handleSearch();
});

const syncMateUuid = () => {
  selectedMateIdArr.value = props.atMateIds.map((item) => item.uuid); // uuid数组是为了判断是否选中
  selectedMateArr.value = [...props.atMateIds]; // Mate的对象数组，之所以复制过来，是因为只有确定后才能修改props.atMateIds
};

// select的Mate直接返回到父组件
const selectMate = (mate: Pick<Mate, "name" | "uuid">) => {
  if (
    selectedMateIdArr.value.length >= MAX_SELECTED_COUNT &&
    !selectedMateIdArr.value.includes(mate.uuid)
  ) {
    showFailToast(i18n.global.t("Message.currentMaxTen"));
    return;
  }

  let atMateArr: Pick<Mate, "name" | "uuid">[] = [...selectedMateArr.value];
  if (selectedMateIdArr.value.includes(mate.uuid)) {
    // 直接同步接口需求
    atMateArr = atMateArr.filter((item) => item.uuid != mate.uuid);
    selectedMateIdArr.value = selectedMateIdArr.value.filter(
      (uuid) => uuid != mate.uuid
    );
  } else {
    atMateArr.push({
      name: mate.name,
      uuid: mate.uuid,
    });
    selectedMateIdArr.value.push(mate.uuid);
  }

  selectedMateArr.value = atMateArr;
};

let timer: number | null = null;
// 防抖函数
const debounce = (delay: number) => {
  if (timer) clearTimeout(timer);

  timer = window.setTimeout(() => {
    handleSearch();
  }, delay);
};

// 发送消息
const handleSearch = async () => {
  isLoading.value = true;
  try {
    await searchStore.initMateListIfEmpty(searchMsg.value);
  } finally {
    isLoading.value = false;
  }
};

const confirm = () => {
  emit("update:atMateIds", [...selectedMateArr.value]);
  emit("update:show", false);
};
</script>
<template>
  <popup
    :show="show"
    class="flex flex-col gap-4 h-[35rem]"
    position="bottom"
    round
    teleport="#main-view"
    @open="syncMateUuid"
    @update:show="emit('update:show', $event)"
  >
    <div class="flex flex-col h-full">
      <div class="flex flex-col pb-2">
        <div class="navbar h-fit">
          <div class="navbar-start"></div>
          <div
            class="flex items-center justify-center flex-grow whitespace-nowrap font-bold"
          >
            {{ $t("Message.choosePeopleRemind") }}
          </div>

          <div class="navbar-end">
            <button class="btn btn-xs btn-primary rounded" @click="confirm">
              {{ $t("Buttons.confirm") }} {{ selectedMateIdArr.length }}/{{
                MAX_SELECTED_COUNT
              }}
            </button>
          </div>
        </div>
        <div class="overflow-hidden rounded-md mx-4">
          <Search
            v-model="searchMsg"
            :placeholder="$t('Message.search')"
            class="p-1 after:hidden"
            @clear="handleSearch"
            @input="debounce(500)"
            @search="handleSearch"
          />
        </div>
      </div>
      <div
        v-show="selectedMateArr.length > 0"
        class="py-2 px-4 flex gap-2 flex-wrap"
      >
        <Tag
          v-for="atMate in selectedMateArr"
          :key="atMate.uuid"
          class="badge badge-neutral"
          closeable
          @click="selectMate(atMate)"
          @close="selectMate(atMate)"
        >
          {{ atMate.name }}
        </Tag>
      </div>
      <div class="flex-grow overflow-hidden relative">
        <!-- :on-scroll-load="() => remindMates.fetchMoreMomentList(keyword)" -->
        <div
          v-if="isLoading"
          class="h-[200px] flex items-center justify-center"
        >
          <span class="loading"></span>
        </div>
        <div
          v-else-if="searchStore.mateList.length > 0"
          class="h-full overflow-y-scroll relative"
        >
          <thread-list
            ref="followingListRef"
            :enable-pull-load="false"
            :on-scroll-load="() => searchStore.fetchMoreMateList(searchMsg)"
            class="px-2"
          >
            <div
              v-for="mate in searchStore.mateList"
              :key="mate.uuid"
              class="flex items-center"
              @click="selectMate(mate)"
            >
              <input
                :checked="selectedMateIdArr.includes(mate.uuid)"
                class="checkbox checkbox-sm rounded-full checked:checkbox-success"
                type="checkbox"
              />
              <div class="flex p-2 border-b gap-2 flex-1 items-center">
                <user-avatar
                  :avatar-url="mate.mediaUrl"
                  class="h-10 shrink-0 mr-2"
                />
                <div class="flex flex-col flex-grow justify-between h-full">
                  <div class="w-full flex justify-between items-center">
                    <div class="flex flex-col">
                      <div class="flex items-center gap-1">
                        <div class="font-bold">{{ mate.name }}</div>
                        <gender-badge :gender="mate.gender" class="w-5" />
                      </div>
                    </div>
                  </div>
                  <p
                    v-html="
                      mate.bio ? mate.bio : $t('Texts.noIntroductionText')
                    "
                    class="text-xs line-clamp-1 text-base-content/50"
                  ></p>
                  <!-- <TextEllipsis
                    :content="
                      mate.bio ? mate.bio : $t('Texts.noIntroductionText')
                    "
                    class="text-xs text-base-content/50"
                    rows="1"
                  /> -->
                </div>
              </div>
            </div>
          </thread-list>
        </div>
        <div
          v-else
          class="flex flex-col items-center justify-center h-[200px] w-full"
        >
          <div class="flex flex-col items-center justify-center">
            <div class="text-2xl font-bold">
              {{ $t("Message.noMateAvailable") }}
            </div>
            <div class="text-neutral-400">
              {{ $t("Message.noMateChangeKeywrod") }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </popup>
</template>
